import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import { Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Input } from "Components/Input/Input";
import { Button } from "Components/Button/Button";
import { useMutation } from "Hooks/useMutation";
import { patchUsuarioPerfil } from "Services/usuario_service";
import "./UserForm.scss";

const userSchema = Yup.object({
  telefono: Yup.string().matches(
    /^\d{8}$/,
    "El teléfono debe tener exactamente 8 dígitos"
  ),
  correo: Yup.string().email("Ingresa un correo electrónico válido"),
});

export const UserForm = ({
  className = "usuario__form",
  perfilData,
  idUsuario,
  perfilLoading,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [initialValues, setInitialValues] = useState({
    dpi: "",
    nombreCompleto: "",
    fechaNacimiento: "",
    telefono: "",
    correo: "",
  });

  const { isLoading, mutate: updatePerfil } = useMutation(patchUsuarioPerfil);

  const formik = useFormik({
    initialValues,
    validationSchema: userSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  const handleTelefonoChange = (e) => {
    const numbersOnly = e.target.value.replace(/\D/g, "");
    formik.setFieldValue("telefono", numbersOnly);
  };

  const handleSubmit = async (values) => {
    const hasChanges =
      formik.values.telefono !== initialValues.telefono ||
      formik.values.correo !== initialValues.correo;

    if (!hasChanges) {
      showSnackbar("No has realizado ningún cambio", "info");
      return;
    }

    try {
      const update = {
        idUsuario,
        telefono: values.telefono || "",
        correo: values.correo || "",
      };

      const response = await updatePerfil(update);

      if (response.data) {
        setInitialValues((prev) => ({
          ...prev,
          telefono: response.data.telefono || "",
          correo: response.data.correo || "",
        }));

        showSnackbar("¡Tus cambios han sido guardados con éxito!", "success");
        setIsEditing(false);
      } else {
        showSnackbar(
          response?.message || "Ocurrió un error al guardar los cambios",
          "error"
        );
      }
    } catch (error) {
      showSnackbar(
        "Ocurrió un error al guardar los cambios. Intenta más tarde.",
        "error"
      );
    }
  };

  const handleRegresar = (e) => {
    e.preventDefault();
    navigate("/");
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleClick = (e) => {
    if (!isEditing) {
      e.preventDefault();
      setIsEditing(true);
    }
  };

  useEffect(() => {
    if (perfilData) {
      setInitialValues(perfilData);
    }
  }, [perfilData]);

  return (
    <>
      <Form onSubmit={formik.handleSubmit} noValidate className={className}>
        <div className="usuario__form-section">
          <h2 className="usuario__form-header">Tus datos</h2>
          <h4 className="usuario__form-title">Tu información</h4>

          <Input
            label="DPI"
            name="dpi"
            id="dpi"
            type="text"
            placeholder="0000 00000 0000"
            value={formik.values.dpi}
            onChange={formik.handleChange}
            disabled={true}
            maxLength={13}
            autoComplete="off"
            isLoading={perfilLoading}
          />

          <Input
            label="Nombre"
            name="nombreCompleto"
            id="nombreCompleto"
            type="text"
            placeholder="Nombre completo"
            value={formik.values.nombreCompleto}
            onChange={formik.handleChange}
            disabled={true}
            autoComplete="name"
            isLoading={perfilLoading}
          />

          <Input
            label="Fecha de nacimiento"
            name="fechaNacimiento"
            id="fechaNacimiento"
            type="date"
            value={formik.values.fechaNacimiento}
            onChange={formik.handleChange}
            disabled={true}
            autoComplete="bday"
            isLoading={perfilLoading}
          />

          <h4 className="usuario__form-title">Tu información editable</h4>

          <Input
            label="Número de teléfono"
            name="telefono"
            id="telefono"
            type="tel"
            placeholder="5523 0022"
            value={formik.values.telefono}
            onChange={handleTelefonoChange}
            onBlur={formik.handleBlur}
            isInvalid={formik.touched.telefono && !!formik.errors.telefono}
            errorMessage={formik.touched.telefono ? formik.errors.telefono : ""}
            disabled={!isEditing || isLoading}
            maxLength={8}
            autoComplete="number"
            isLoading={perfilLoading}
          />

          <Input
            label="Correo electrónico"
            name="correo"
            id="correo"
            type="email"
            placeholder="ejemplo@correo.com"
            value={formik.values.correo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={formik.touched.correo && !!formik.errors.correo}
            errorMessage={formik.touched.correo ? formik.errors.correo : ""}
            disabled={!isEditing || isLoading}
            autoComplete="email"
            isLoading={perfilLoading}
          />
        </div>

        <div className="usuario__form-actions">
          <Button
            type={isEditing ? "submit" : "button"}
            variant="secundario-cta"
            disabled={isLoading || perfilLoading || !Boolean(idUsuario)}
            onClick={handleClick}
          >
            {isEditing ? "GUARDAR CAMBIOS" : "EDITAR"}
          </Button>

          <Button
            variant="secundario"
            disabled={isLoading}
            onClick={handleRegresar}
          >
            REGRESAR
          </Button>
        </div>
      </Form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
